import type es from '../es/partner';

/** `partner` in English. Same keys as `es/partner.ts`, enforced by the type. */
const partner: typeof es = {
  scope: 'TBit Jobs',
  title: 'Partner program',
  lede: 'Recommend TBit Jobs with your code and earn for every person who subscribes.',
  loading: 'Loading the partner program',
  loadError: "We couldn't load the program. Reload the page.",

  how: {
    scope: 'How it works',
    discount: 'Anyone who subscribes with your code or your link gets USD 7 off their first month.',
    commission: 'You get USD 5 for every month that person pays, for as long as they stay subscribed.',
    /** The full rules: the program terms, published on tbit.app. */
    terms: { label: 'Read the Partner Program Terms', href: 'https://tbit.app/legal/tbit-jobs-partner-program-terms' },
    payouts: "What you earn is credited to you when your referral pays, and Stripe deposits it into your bank account on your payout schedule, usually once a month (with a minimum amount in some countries). Once you're approved, you set up your payouts in a Stripe form.",
  },

  form: {
    scope: 'Your application',
    title: 'I want to be a partner',
    name: 'Your name',
    email: 'Your email',
    emailHint: "It's the one you're signed in with. We'll write to you there once we review your application.",
    whatsapp: 'Your WhatsApp',
    whatsappHint: 'Include the country code, for example +1 305 123 4567.',
    country: "Country where you'll receive payments",
    countryPlaceholder: 'Choose your country',
    countryHint: "It can't be changed later: it's the country of your payout account.",
    countriesWePay: 'We can pay you in:',
    countryMissing: "Don't see your country? For now Stripe doesn't let us pay in Honduras, Venezuela, Nicaragua or Brazil. Write to us and we'll look for another way.",
    community: 'Your agency or community (optional)',
    communityPlaceholder: 'For example, H2B Visas Colombia',
    plan: 'How will you promote TBit Jobs?',
    planPlaceholder: 'Tell us where and to whom: your Facebook group, your YouTube channel, your office…',
    code: 'The code you want',
    codeHint: "Letters and numbers only, 3 to 24. It's your coupon and it goes in your link.",
    submit: 'Send my application',
    /** The REQUIRED checkbox for the program terms, above the button. */
    terms: {
      before: 'I accept the ',
      label: 'TBit Jobs Partner Program Terms',
      href: 'https://tbit.app/legal/tbit-jobs-partner-program-terms',
      after: '.',
      required: 'Check the box to accept the program terms.',
      outdated: 'We updated the terms. Read them and check the box again.',
    },
    submitting: 'Sending…',
    errors: {
      name: 'Write your name.',
      whatsapp: 'Check your WhatsApp: it needs the country code.',
      country: 'Choose a country from the list.',
      community_name: 'Check the name of your agency or community.',
      promotion_plan: 'Tell us a little more (at least one sentence).',
      code: 'Use letters and numbers only, 3 to 24.',
      codeTaken: 'Someone already has that code. Try another one.',
      failed: "We couldn't send your application. Try again in a moment.",
    },
  },

  pending: {
    title: 'We got your application',
    body: (code: string) =>
      `We're reviewing it and will email you once it's done. The code you asked for is ${code}.`,
  },

  rejected: {
    title: 'Your application was not approved',
    reason: (reason: string) => `Reason: ${reason}`,
    retry: 'If something changed, you can fix it and send it again.',
  },

  panel: {
    paused:
      "Your code is paused: it doesn't give new people a discount, and monthly payments made during the pause don't earn a commission. What you already earned keeps being paid to you. Write to us if you have questions.",
    terminated:
      'Your participation in the program has ended. We pay you the commissions you earned while you were active, once your payout account is enabled.',
    payoutsHeld: "We're holding your payouts while we review your account. Write to us at support@tbit.app.",
    terms: {
      title: (version: string) => `Accept the Partner Program Terms (version ${version})`,
      body: "To stay in the program, read and accept the current version of the terms. A change to the terms never alters the conditions of the referrals you've already brought.",
      accept: 'Accept the terms',
      accepting: 'Saving…',
      failed: "We couldn't save your acceptance. Try again in a moment.",
    },
    codeScope: 'Your code',
    linkLabel: 'Your link',
    copy: 'Copy link',
    copied: 'Copied',
    shareNote: 'Anyone who comes in through your link or types your code at checkout stays with you, with USD 7 off their first month.',
    payouts: {
      scope: 'Your payouts',
      none: "You haven't set up your payouts yet. What you earn is saved and we pay it as soon as you finish.",
      pending: 'Stripe is still reviewing your details, or the form is not finished yet.',
      ready: "All set. What you earn is credited when each person you brought pays for the month, and Stripe deposits it into your bank on your payout schedule.",
      setup: 'Set up my payouts',
      continue: 'Continue with Stripe',
      dashboard: 'See my payments on Stripe',
      opening: 'Opening…',
      failed: "We couldn't open Stripe. Try again in a moment.",
      unsupportedCountry: "Stripe can't pay out in the country on your application. Write to us and we'll sort it out with you.",
      status: {
        none: 'Not set up',
        pending: 'Pending',
        ready: 'Ready',
      },
    },
    totals: {
      paid: 'Paid',
      pending: 'To be paid',
      failed: 'Retrying',
    },
    referrals: {
      scope: 'Your referrals',
      count: (n: number) => (n === 1 ? '1 person' : `${n} people`),
      registered: (n: number) =>
        n === 1 ? '1 person signed up with your code' : `${n} people signed up with your code`,
      followUp: 'Write to them to help them finish their resume or start their trial. They know you can see their email and their plan status.',
      empty: 'Nobody has signed up with your code yet.',
      emptyNote: 'Share your link: everyone who signs up shows up here.',
      emptyFilter: 'Nobody in this status right now.',
      hidden:
        "While your code is paused, or if your participation has ended, you can't see your referrals list. Your payouts and commissions are still here, and what you earned keeps being paid to you.",
      filterLabel: 'Filter referrals by status',
      all: 'All',
      columns: { name: 'Name', email: 'Email', registered: 'Signed up', state: 'Status', terms: 'Terms' },
      termsVersion: (version: string) => `v. ${version}`,
      termsNote: 'Terms: the version of the terms each person came in under. Their commissions follow those rules even if the terms change.',
      state: {
        paying: 'Paying',
        trial: 'On trial',
        no_trial: 'No active trial',
        canceled: 'Canceled',
        courtesy: 'Courtesy',
      },
      trialEnds: (date: string) => `Ends on ${date}`,
      courtesyNote: 'Courtesy: a plan TBit gave them for free. It earns no commission.',
    },
    commissions: {
      scope: 'Your commissions',
      empty: 'No commissions yet. They come in when someone you brought pays their first month.',
      columns: { date: 'Date', amount: 'Amount', status: 'Status' },
      status: {
        PENDING: 'Pending',
        PAID: 'Paid',
        FAILED: "Failed, we're retrying",
        VOID: 'Voided',
      },
      voidReason: {
        refunded: 'Voided: that person was refunded',
        disputed: 'Voided: that person disputed the charge with their bank',
      } as Record<string, string>,
    },
  },
};

export default partner;
